import type { Integration } from '../types/integration'
import type { IJackHenrySilverlakeIntegrationConfig } from '../types/jack-henry-silverlake'
import type { IJackHenrySynergyIntegrationConfig } from '../types/jack-henry-synergy'
import {
  OE_HEADER_AUDIT_USER_ID,
  OE_HEADER_AUDIT_WORKSTATION_ID,
  OE_HEADER_INSTITUTION_ENVIRONMENT,
  OE_HEADER_INSTITUTION_ID,
  OE_HEADER_ROUTING_ID,
  OE_HEADER_VALID_CONSUMER_NAME,
  OE_HEADER_VALID_CONSUMER_PRODUCT
} from './oe-headers'

export const JACK_HENRY_SILVERLAKE_REQUEST_CONTEXT_DB_FIELDS = [
  'routingId',
  'institutionEnvironment',
  'validConsumerName',
  'validConsumerProduct',
  'auditUserId',
  'auditWorkstationId'
] as const satisfies readonly (keyof IJackHenrySilverlakeIntegrationConfig)[]

export const JACK_HENRY_SYNERGY_REQUEST_CONTEXT_DB_FIELDS = [
  'institutionId',
  'institutionEnvironment'
] as const satisfies readonly (keyof IJackHenrySynergyIntegrationConfig)[]

export type JackHenrySilverlakeRequestContextDbField =
  (typeof JACK_HENRY_SILVERLAKE_REQUEST_CONTEXT_DB_FIELDS)[number]

export type JackHenrySynergyRequestContextDbField =
  (typeof JACK_HENRY_SYNERGY_REQUEST_CONTEXT_DB_FIELDS)[number]

export const JACK_HENRY_SILVERLAKE_REQUEST_CONTEXT_FIELD_HEADERS: Record<
  JackHenrySilverlakeRequestContextDbField,
  string
> = {
  routingId: OE_HEADER_ROUTING_ID,
  institutionEnvironment: OE_HEADER_INSTITUTION_ENVIRONMENT,
  validConsumerName: OE_HEADER_VALID_CONSUMER_NAME,
  validConsumerProduct: OE_HEADER_VALID_CONSUMER_PRODUCT,
  auditUserId: OE_HEADER_AUDIT_USER_ID,
  auditWorkstationId: OE_HEADER_AUDIT_WORKSTATION_ID
}

export const JACK_HENRY_SYNERGY_REQUEST_CONTEXT_FIELD_HEADERS: Record<
  JackHenrySynergyRequestContextDbField,
  string
> = {
  institutionId: OE_HEADER_INSTITUTION_ID,
  institutionEnvironment: OE_HEADER_INSTITUTION_ENVIRONMENT
}

export function canManageIntegrationStoredFields(role: string | undefined): boolean {
  return role === 'administrator'
}

export const canManageIntegrationRequestContextFields = canManageIntegrationStoredFields

export function isIntegrationEnableMarker(body: unknown): boolean {
  return (
    typeof body === 'object' &&
    body !== null &&
    !Array.isArray(body) &&
    Object.keys(body).length === 0
  )
}

export function sanitizeStoredConfigBodyForRole<T>(
  body: unknown,
  role: string | undefined
): T | undefined | null {
  if (body === null) {
    return null
  }
  if (body === undefined) {
    return undefined
  }
  if (!canManageIntegrationStoredFields(role)) {
    if (isIntegrationEnableMarker(body)) {
      return {} as T
    }
    return undefined
  }
  return body as T
}

export function omitIntegrationStoredConfigFields(integration: Integration): Integration {
  const next = { ...integration }
  delete next.jackHenrySilverlakeConfig
  delete next.jackHenrySynergyConfig
  delete next.equifaxPrequalConfig
  delete next.equifaxDITConfig
  return next
}

/** @deprecated use omitIntegrationStoredConfigFields */
export function omitIntegrationRequestContextFields(integration: Integration): Integration {
  return omitIntegrationStoredConfigFields(integration)
}
